/**
 * feature-flags.ts — minimal client-side feature-flag registry.
 *
 * Flags are read from (in order of precedence):
 *   1. `localStorage` key `ff:<flag>` (string `"true"` / `"false"`) — lets
 *      QA toggle a flag without rebuilding.
 *   2. Vite env var `VITE_FF_<FLAG_UPPER>` — set at build time per env.
 *   3. The default in `FEATURE_FLAGS`.
 *
 * This is intentionally tiny. When we need cohort/percentage rollout we'll
 * back it with the existing `/api/billing/subscription` payload or a real
 * flag service; for the dogfood week of web-search we just need an on/off.
 *
 * ENTITLEMENT RULE (audited 2026-06-03): flags here gate UI ROLLOUT ONLY,
 * never a paid capability. Every flag can be flipped by anyone via the
 * `ff:<flag>` localStorage override, so using one to unlock a paid feature
 * would be trivially bypassable. Paid gating is enforced server-side — the
 * API returns `TIER_REQUIRED` / `MONTHLY_LIMIT_REACHED` codes (or HTTP 402)
 * and the client only reflects that decision (see useAIAction.ts). Do NOT
 * add a flag that, when forced on client-side, grants access to a metered or
 * tier-restricted capability.
 */

export const FEATURE_FLAGS = {
  /** Phase B web-search research surfaces (paper cards, find-papers action). */
  feature_web_search_ui: false,
  /**
   * Iterate v2 — "Plan next test" routes to a ResearchInterview re-entry
   * conversation instead of the IteratePage option-cards.
   * Kill criterion: if hypothesis-to-submission rate from re-entry drops below
   * the IteratePage baseline after 30 days, revert.
   */
  ff_iterate_v2: true,
  /**
   * Live trading v1 — gates whether the "Promote to Live Capital" UI is
   * OFFERED AT ALL (a rollout/preview toggle, same as every other flag in
   * this file — see the ENTITLEMENT RULE at the top). Simulation (paper)
   * deployments do NOT require this flag.
   *
   * This flag alone no longer decides whether the promote button looks
   * clickable: `DeploymentDetail.tsx` also calls `usePlatformCapabilities()`
   * (`GET /api/platform/capabilities` -> `live_trading_enabled`, sourced
   * from the server's real `LIVE_TRADING_ENABLED` kill switch,
   * `program/src/api/deployment.rs`). When this flag is on but the server
   * capability is off, the UI shows the friendly "not enabled yet" message
   * immediately instead of a dead clickable button — a tenant used to be
   * able to flip this flag, spend a week satisfying the paper-trading
   * requirement, and only then hit a hard `live_trading_disabled` rejection
   * at the very last step. Kept (rather than deleted now that a real signal
   * exists) because it still has a legitimate use: previewing the promote
   * UI in a staging environment ahead of the server flag flipping on.
   * Kill criterion: if >2 unintended live orders are placed due to system
   * error in the first 30 days, disable and investigate. Enable per-tenant
   * via localStorage ff:ff_live_trading_v1=true or VITE_FF_FF_LIVE_TRADING_V1=true.
   */
  ff_live_trading_v1: false,
  /**
   * Market-data feed status badge on deployments (council ruling 2026-07-03).
   * Server computes data_status ("live"|"delayed"|"no_data"|"market_closed")
   * from SignalEngine's per-deployment heartbeat; the badge renders it on the
   * Deployments page, dashboard cards, and deployment detail. Crypto is the
   * only asset class that can show the red no-data state in v1.
   * Kill criterion: >1 false "No market data" per week on a healthy feed
   * after 2 weeks of threshold tuning — pull the flag, keep the heartbeat.
   */
  ff_data_heartbeat: true,
  /**
   * Strategy vitals line (design council 2026-07-04): "Watching SUI-USD ·
   * ~14 price updates/min · last checked 16s ago" on deployment detail +
   * list rows. Answers "is my strategy alive?" via the evaluation axis —
   * data_rate_per_min + last_data_at from the deployments API. Values update
   * only when polls land (no fake real-time animation).
   * Kill criterion: if it doesn't reduce "is it working?" checks after 30
   * days (telemetry: vitals rendered vs manual refresh rate), drop it.
   */
  ff_strategy_vitals: true,
  /**
   * Research side pane v1: the generated-strategy preview (hypothesis recap,
   * code toggle, BacktestConfigPanel, run/regenerate actions) renders in a
   * persistent right-side pane next to the chat on desktop, instead of
   * inline below the message list. Desktop-only — useDeviceType() gates
   * mobile/tablet back to the inline footer regardless of this flag. Default
   * pane state is open (no separate collapse toggle).
   * Kill criterion: if backtest-submission rate from the preview drops after
   * 30 days vs. the inline-footer baseline, revert.
   */
  ff_research_side_pane: true,
  /**
   * Mandate UI v1 -- the mandate wizard, documented-strategy library and
   * proposal/approval pages (Mandate, Library and Proposals in the sidebar).
   * /api/mandates, /api/library and /api/proposals are all real, wired and
   * unconditionally mounted on main (mandate-api/mandate-validator work +
   * W7.1 ReplicationRunSleeveHistory, confirmed 2026-10-14 -- proposal
   * creation no longer hits the NoSleeveHistory stub). Enabled 2026-09-30
   * (owner decision, PR #186) and kept on since; this flag is no longer
   * staging a rollout, it's a permanent per-browser emergency kill switch,
   * the same role ff_data_heartbeat/ff_strategy_vitals/ff_research_side_pane
   * play below. In mock mode (VITE_USE_MOCK_API=true) the UI is always on
   * regardless of this flag; see src/mandate/enabled.ts. Per-browser
   * override still available: localStorage ff:ff_mandate_v1=false to hide it
   * again for one browser.
   */
  ff_mandate_v1: true,
} as const;

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

function readLocalStorage(flag: FeatureFlagKey): boolean | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(`ff:${flag}`);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch {
    /* SSR / private mode */
  }
  return null;
}

function readEnv(flag: FeatureFlagKey): boolean | null {
  const key = `VITE_FF_${flag.toUpperCase()}` as keyof ImportMetaEnv;
  const val = import.meta.env[key];
  if (val === 'true' || val === true) return true;
  if (val === 'false' || val === false) return false;
  return null;
}

export function isFeatureEnabled(flag: FeatureFlagKey): boolean {
  const ls = readLocalStorage(flag);
  if (ls !== null) return ls;
  const env = readEnv(flag);
  if (env !== null) return env;
  return FEATURE_FLAGS[flag];
}

/** Test/dev helper — sets the localStorage override. */
export function setFeatureFlag(flag: FeatureFlagKey, value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`ff:${flag}`, value ? 'true' : 'false');
  } catch {
    /* ignore */
  }
}

